import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Textarea } from '@client/components/ui/textarea';
import { Switch } from '@client/components/ui/switch';
import { Badge } from '@client/components/ui/badge';
import { Separator } from '@client/components/ui/separator';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  ArrowUp, 
  ArrowDown, 
  Search,
  Users,
  Building2,
  Eye,
  EyeOff
} from 'lucide-react';
import { GenderValue, IndustryValue, defaultGenderValues, defaultIndustryValues } from '@shared/defaults/';

interface DataValuesTabProps {
  // Basierend auf Ihrem Pattern - keine direkte Settings-Integration nÃ¶tig
  // da Gender/Industry Values in-memory verwaltet werden wie Categories/ScoreRanges
}

interface EditingValue {
  type: 'gender' | 'industry';
  id: string | null; // null = new value
  data: Partial<GenderValue | IndustryValue>;
}

export default function DataValuesTab({}: DataValuesTabProps) {
  // State fÃ¼r Gender und Industry Values (in-memory Management)
  const [genderValues, setGenderValues] = useState<GenderValue[]>(defaultGenderValues);
  const [industryValues, setIndustryValues] = useState<IndustryValue[]>(defaultIndustryValues);
  
  // UI State
  const [editingValue, setEditingValue] = useState<EditingValue | null>(null);
  const [searchGender, setSearchGender] = useState('');
  const [searchIndustry, setSearchIndustry] = useState('');
  const [showInactiveGenders, setShowInactiveGenders] = useState(false);
  const [showInactiveIndustries, setShowInactiveIndustries] = useState(false);

  // ===== HELPER FUNCTIONS =====

  const generateId = (label: string): string => {
    return label.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  };

  const getNextOrder = (values: (GenderValue | IndustryValue)[]): number => {
    return Math.max(...values.map(v => v.order || 0)) + 1;
  };

  // ===== GENDER VALUE FUNCTIONS =====

  const addGenderValue = () => {
    setEditingValue({
      type: 'gender',
      id: null,
      data: {
        label: '',
        order: getNextOrder(genderValues),
        isActive: true,
        description: ''
      }
    });
  };

  const editGenderValue = (gender: GenderValue) => {
    setEditingValue({
      type: 'gender',
      id: gender.id,
      data: { ...gender }
    });
  };

  const saveGenderValue = () => {
    if (!editingValue || editingValue.type !== 'gender') return;
    
    const data = editingValue.data as Partial<GenderValue>;
    if (!data.label?.trim()) return;

    const id = editingValue.id || generateId(data.label);
    const newValue: GenderValue = {
      id,
      label: data.label.trim(),
      order: data.order || getNextOrder(genderValues),
      isActive: data.isActive ?? true,
      description: data.description || ''
    };

    if (editingValue.id) {
      // Update existing
      setGenderValues(prev => prev.map(g => g.id === editingValue.id ? newValue : g));
    } else {
      // Add new
      setGenderValues(prev => [...prev, newValue]);
    }
    
    setEditingValue(null);
  };

  const deleteGenderValue = (id: string) => {
    if (confirm('Gender-Wert wirklich lÃ¶schen?')) {
      setGenderValues(prev => prev.filter(g => g.id !== id));
    }
  };

  const toggleGenderActive = (id: string) => {
    setGenderValues(prev => prev.map(g => 
      g.id === id ? { ...g, isActive: !g.isActive } : g
    ));
  };

  const moveGenderValue = (id: string, direction: 'up' | 'down') => {
    setGenderValues(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex(g => g.id === id);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= sorted.length) return prev;
      
      // Swap orders
      const temp = sorted[index].order;
      sorted[index].order = sorted[newIndex].order;
      sorted[newIndex].order = temp;
      
      return sorted;
    });
  };

  // ===== INDUSTRY VALUE FUNCTIONS =====

  const addIndustryValue = () => {
    setEditingValue({
      type: 'industry',
      id: null,
      data: {
        label: '',
        order: getNextOrder(industryValues),
        isActive: true,
        description: ''
      }
    });
  };

  const editIndustryValue = (industry: IndustryValue) => {
    setEditingValue({
      type: 'industry',
      id: industry.id,
      data: { ...industry }
    });
  };

  const saveIndustryValue = () => {
    if (!editingValue || editingValue.type !== 'industry') return;
    
    const data = editingValue.data as Partial<IndustryValue>;
    if (!data.label?.trim()) return;

    const id = editingValue.id || generateId(data.label);
    const newValue: IndustryValue = {
      id,
      label: data.label.trim(),
      order: data.order || getNextOrder(industryValues),
      isActive: data.isActive ?? true,
      description: data.description || ''
    };

    if (editingValue.id) {
      // Update existing
      setIndustryValues(prev => prev.map(i => i.id === editingValue.id ? newValue : i));
    } else {
      // Add new
      setIndustryValues(prev => [...prev, newValue]);
    }
    
    setEditingValue(null);
  };

  const deleteIndustryValue = (id: string) => {
    if (confirm('Branchen-Wert wirklich lÃ¶schen?')) {
      setIndustryValues(prev => prev.filter(i => i.id !== id));
    }
  };

  const toggleIndustryActive = (id: string) => {
    setIndustryValues(prev => prev.map(i => 
      i.id === id ? { ...i, isActive: !i.isActive } : i
    ));
  };

  const moveIndustryValue = (id: string, direction: 'up' | 'down') => {
    setIndustryValues(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex(i => i.id === id);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= sorted.length) return prev;
      
      // Swap orders
      const temp = sorted[index].order;
      sorted[index].order = sorted[newIndex].order;
      sorted[newIndex].order = temp;
      
      return sorted;
    });
  };

  // ===== FILTER FUNCTIONS =====

  const filteredGenderValues = genderValues
    .filter(g => showInactiveGenders || g.isActive !== false)
    .filter(g => 
      searchGender === '' || 
      g.label.toLowerCase().includes(searchGender.toLowerCase()) ||
      g.description?.toLowerCase().includes(searchGender.toLowerCase())
    )
    .sort((a, b) => a.order - b.order);

  const filteredIndustryValues = industryValues
    .filter(i => showInactiveIndustries || i.isActive !== false)
    .filter(i => 
      searchIndustry === '' || 
      i.label.toLowerCase().includes(searchIndustry.toLowerCase()) ||
      i.description?.toLowerCase().includes(searchIndustry.toLowerCase())
    )
    .sort((a, b) => a.order - b.order);

  // ===== CANCEL EDITING =====

  const cancelEditing = () => {
    setEditingValue(null);
  };

  // ===== STATISTICS =====

  const genderStats = {
    total: genderValues.length,
    active: genderValues.filter(g => g.isActive).length,
    inactive: genderValues.filter(g => !g.isActive).length
  };

  const industryStats = {
    total: industryValues.length,
    active: industryValues.filter(i => i.isActive).length,
    inactive: industryValues.filter(i => !i.isActive).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Datenwerte-Verwaltung</h2>
          <p className="text-muted-foreground">
            Verwaltung von Gender- und Branchen-Werten fÃ¼r Profile
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gender-Werte</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{genderStats.total}</div>
            <p className="text-xs text-muted-foreground">
              {genderStats.active} aktiv, {genderStats.inactive} inaktiv
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Branchen-Werte</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{industryStats.total}</div>
            <p className="text-xs text-muted-foreground">
              {industryStats.active} aktiv, {industryStats.inactive} inaktiv
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GENDER VALUES SECTION */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <CardTitle>Gender-Werte</CardTitle>
              </div>
              <Button onClick={addGenderValue} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                HinzufÃ¼gen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search and Filters */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Gender-Werte durchsuchen..."
                  value={searchGender}
                  onChange={(e) => setSearchGender(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-inactive-genders"
                  checked={showInactiveGenders}
                  onCheckedChange={setShowInactiveGenders}
                />
                <label htmlFor="show-inactive-genders" className="text-sm">
                  Inaktive anzeigen
                </label>
              </div>
            </div>

            <Separator />

            {/* Gender Values List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredGenderValues.map((gender) => (
                <div
                  key={gender.id}
                  className={`p-3 border rounded-lg ${
                    !gender.isActive ? 'opacity-60 bg-muted/50' : ''
                  }`}
                >
                  {editingValue?.type === 'gender' && editingValue?.id === gender.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Label"
                          value={(editingValue.data as GenderValue).label || ''}
                          onChange={(e) =>
                            setEditingValue(prev => prev ? {
                              ...prev,
                              data: { ...prev.data, label: e.target.value }
                            } : null)
                          }
                        />
                        <Input
                          type="number"
                          placeholder="Reihenfolge"
                          value={(editingValue.data as GenderValue).order || ''}
                          onChange={(e) =>
                            setEditingValue(prev => prev ? {
                              ...prev,
                              data: { ...prev.data, order: parseInt(e.target.value) || 0 }
                            } : null)
                          }
                        />
                      </div>
                      <Textarea
                        placeholder="Beschreibung (optional)"
                        value={(editingValue.data as GenderValue).description || ''}
                        onChange={(e) =>
                          setEditingValue(prev => prev ? {
                            ...prev,
                            data: { ...prev.data, description: e.target.value }
                          } : null)
                        }
                        rows={2}
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={(editingValue.data as GenderValue).isActive ?? true}
                            onCheckedChange={(checked) =>
                              setEditingValue(prev => prev ? {
                                ...prev,
                                data: { ...prev.data, isActive: checked }
                              } : null)
                            }
                          />
                          <span className="text-sm">Aktiv</span>
                        </div>
                        <div className="flex space-x-2">
                          <Button onClick={saveGenderValue} size="sm">
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button onClick={cancelEditing} variant="outline" size="sm">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{gender.label}</span>
                          <Badge variant="outline" className="text-xs">
                            #{gender.order}
                          </Badge>
                          {!gender.isActive && (
                            <Badge variant="secondary" className="text-xs">
                              Inaktiv
                            </Badge>
                          )}
                        </div>
                        {gender.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {gender.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <Button
                          onClick={() => toggleGenderActive(gender.id)}
                          variant="ghost"
                          size="sm"
                          title={gender.isActive ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          {gender.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          onClick={() => moveGenderValue(gender.id, 'up')}
                          variant="ghost"
                          size="sm"
                          title="Nach oben"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => moveGenderValue(gender.id, 'down')}
                          variant="ghost"
                          size="sm"
                          title="Nach unten"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => editGenderValue(gender)}
                          variant="ghost"
                          size="sm"
                          title="Bearbeiten"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteGenderValue(gender.id)}
                          variant="ghost"
                          size="sm"
                          title="LÃ¶schen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* New Gender Value Form */}
              {editingValue?.type === 'gender' && editingValue?.id === null && (
                <div className="p-3 border rounded-lg bg-muted/20">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Label"
                        value={(editingValue.data as Partial<GenderValue>).label || ''}
                        onChange={(e) =>
                          setEditingValue(prev => prev ? {
                            ...prev,
                            data: { ...prev.data, label: e.target.value }
                          } : null)
                        }
                      />
                      <Input
                        type="number"
                        placeholder="Reihenfolge"
                        value={(editingValue.data as Partial<GenderValue>).order || ''}
                        onChange={(e) =>
                          setEditingValue(prev => prev ? {
                            ...prev,
                            data: { ...prev.data, order: parseInt(e.target.value) || 0 }
                          } : null)
                        }
                      />
                    </div>
                    <Textarea
                      placeholder="Beschreibung (optional)"
                      value={(editingValue.data as Partial<GenderValue>).description || ''}
                      onChange={(e) =>
                        setEditingValue(prev => prev ? {
                          ...prev,
                          data: { ...prev.data, description: e.target.value }
                        } : null)
                      }
                      rows={2}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={(editingValue.data as Partial<GenderValue>).isActive ?? true}
                          onCheckedChange={(checked) =>
                            setEditingValue(prev => prev ? {
                              ...prev,
                              data: { ...prev.data, isActive: checked }
                            } : null)
                          }
                        />
                        <span className="text-sm">Aktiv</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button onClick={saveGenderValue} size="sm">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button onClick={cancelEditing} variant="outline" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {filteredGenderValues.length === 0 && searchGender && (
                <div className="text-center py-4 text-muted-foreground">
                  Keine Gender-Werte gefunden fÃ¼r "{searchGender}"
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* INDUSTRY VALUES SECTION */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Building2 className="h-5 w-5" />
                <CardTitle>Branchen-Werte</CardTitle>
              </div>
              <Button onClick={addIndustryValue} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                HinzufÃ¼gen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search and Filters */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Branchen durchsuchen..."
                  value={searchIndustry}
                  onChange={(e) => setSearchIndustry(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-inactive-industries"
                  checked={showInactiveIndustries}
                  onCheckedChange={setShowInactiveIndustries}
                />
                <label htmlFor="show-inactive-industries" className="text-sm">
                  Inaktive anzeigen
                </label>
              </div>
            </div>

            <Separator />

            {/* Industry Values List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredIndustryValues.map((industry) => (
                <div
                  key={industry.id}
                  className={`p-3 border rounded-lg ${
                    !industry.isActive ? 'opacity-60 bg-muted/50' : ''
                  }`}
                >
                  {editingValue?.type === 'industry' && editingValue?.id === industry.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Label"
                          value={(editingValue.data as IndustryValue).label || ''}
                          onChange={(e) =>
                            setEditingValue(prev => prev ? {
                              ...prev,
                              data: { ...prev.data, label: e.target.value }
                            } : null)
                          }
                        />
                        <Input
                          type="number"
                          placeholder="Reihenfolge"
                          value={(editingValue.data as IndustryValue).order || ''}
                          onChange={(e) =>
                            setEditingValue(prev => prev ? {
                              ...prev,
                              data: { ...prev.data, order: parseInt(e.target.value) || 0 }
                            } : null)
                          }
                        />
                      </div>
                      <Textarea
                        placeholder="Beschreibung (optional)"
                        value={(editingValue.data as IndustryValue).description || ''}
                        onChange={(e) =>
                          setEditingValue(prev => prev ? {
                            ...prev,
                            data: { ...prev.data, description: e.target.value }
                          } : null)
                        }
                        rows={2}
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={(editingValue.data as IndustryValue).isActive ?? true}
                            onCheckedChange={(checked) =>
                              setEditingValue(prev => prev ? {
                                ...prev,
                                data: { ...prev.data, isActive: checked }
                              } : null)
                            }
                          />
                          <span className="text-sm">Aktiv</span>
                        </div>
                        <div className="flex space-x-2">
                          <Button onClick={saveIndustryValue} size="sm">
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button onClick={cancelEditing} variant="outline" size="sm">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{industry.label}</span>
                          <Badge variant="outline" className="text-xs">
                            #{industry.order}
                          </Badge>
                          {!industry.isActive && (
                            <Badge variant="secondary" className="text-xs">
                              Inaktiv
                            </Badge>
                          )}
                        </div>
                        {industry.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {industry.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <Button
                          onClick={() => toggleIndustryActive(industry.id)}
                          variant="ghost"
                          size="sm"
                          title={industry.isActive ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          {industry.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          onClick={() => moveIndustryValue(industry.id, 'up')}
                          variant="ghost"
                          size="sm"
                          title="Nach oben"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => moveIndustryValue(industry.id, 'down')}
                          variant="ghost"
                          size="sm"
                          title="Nach unten"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => editIndustryValue(industry)}
                          variant="ghost"
                          size="sm"
                          title="Bearbeiten"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteIndustryValue(industry.id)}
                          variant="ghost"
                          size="sm"
                          title="LÃ¶schen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* New Industry Value Form */}
              {editingValue?.type === 'industry' && editingValue?.id === null && (
                <div className="p-3 border rounded-lg bg-muted/20">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Label"
                        value={(editingValue.data as Partial<IndustryValue>).label || ''}
                        onChange={(e) =>
                          setEditingValue(prev => prev ? {
                            ...prev,
                            data: { ...prev.data, label: e.target.value }
                          } : null)
                        }
                      />
                      <Input
                        type="number"
                        placeholder="Reihenfolge"
                        value={(editingValue.data as Partial<IndustryValue>).order || ''}
                        onChange={(e) =>
                          setEditingValue(prev => prev ? {
                            ...prev,
                            data: { ...prev.data, order: parseInt(e.target.value) || 0 }
                          } : null)
                        }
                      />
                    </div>
                    <Textarea
                      placeholder="Beschreibung (optional)"
                      value={(editingValue.data as Partial<IndustryValue>).description || ''}
                      onChange={(e) =>
                        setEditingValue(prev => prev ? {
                          ...prev,
                          data: { ...prev.data, description: e.target.value }
                        } : null)
                      }
                      rows={2}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={(editingValue.data as Partial<IndustryValue>).isActive ?? true}
                          onCheckedChange={(checked) =>
                            setEditingValue(prev => prev ? {
                              ...prev,
                              data: { ...prev.data, isActive: checked }
                            } : null)
                          }
                        />
                        <span className="text-sm">Aktiv</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button onClick={saveIndustryValue} size="sm">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button onClick={cancelEditing} variant="outline" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {filteredIndustryValues.length === 0 && searchIndustry && (
                <div className="text-center py-4 text-muted-foreground">
                  Keine Branchen gefunden fÃ¼r "{searchIndustry}"
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Aktionen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => {
                if (confirm('Alle Gender-Werte auf Standard zurÃ¼cksetzen?')) {
                  setGenderValues(defaultGenderValues);
                }
              }}
              variant="outline"
            >
              Gender-Werte zurÃ¼cksetzen
            </Button>
            <Button
              onClick={() => {
                if (confirm('Alle Branchen-Werte auf Standard zurÃ¼cksetzen?')) {
                  setIndustryValues(defaultIndustryValues);
                }
              }}
              variant="outline"
            >
              Branchen-Werte zurÃ¼cksetzen
            </Button>
            <Button
              onClick={() => {
                const activeGenders = genderValues.filter(g => g.isActive).length;
                const activeIndustries = industryValues.filter(i => i.isActive).length;
                alert(`Aktive Werte:\n- Gender: ${activeGenders}/${genderValues.length}\n- Branchen: ${activeIndustries}/${industryValues.length}`);
              }}
              variant="outline"
            >
              Statistiken anzeigen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help Information */}
      <Card>
        <CardHeader>
          <CardTitle>Hilfe & Informationen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">
            <h4 className="font-medium text-foreground mb-2">Gender-Werte</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Verwendet fÃ¼r die Klassifizierung von Profilen nach Geschlecht</li>
              <li>KÃ¶nnen in Dropdown-Listen und Filtern verwendet werden</li>
              <li>Reihenfolge bestimmt die Sortierung in der BenutzeroberflÃ¤che</li>
            </ul>
          </div>
          
          <div className="text-sm text-muted-foreground">
            <h4 className="font-medium text-foreground mb-2">Branchen-Werte</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Basiert auf der deutschen Wirtschaftszweig-Klassifikation (WZ 2008)</li>
              <li>Erweitert um moderne digitale Branchen und Zukunftstechnologien</li>
              <li>Verwendet fÃ¼r die Kategorisierung von Unternehmen und Profilen</li>
              <li>Inaktive Werte werden aus Dropdown-Listen ausgeblendet</li>
            </ul>
          </div>

          <div className="text-sm text-muted-foreground">
            <h4 className="font-medium text-foreground mb-2">Hinweise</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Ã„nderungen werden nur in-memory gespeichert (wie Categories/Score Ranges)</li>
              <li>FÃ¼r produktive Nutzung sollten die Werte in die Datenbank persistiert werden</li>
              <li>IDs werden automatisch aus dem Label generiert (lowercase, ohne Sonderzeichen)</li>
              <li>Die Reihenfolge kann Ã¼ber die Pfeil-Buttons oder direkt bearbeitet werden</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}